import { ThunkAction } from '~/Store';

import ApiClient from './ApiClient';

export const invokeApi =
	<Result>(action: (apiClient: ApiClient, ...args: any[]) => Result, ...args: any[]): ThunkAction<Result, never> =>
	(dispatch, getState) => {
		const { client } = getState().apiClient;
		if (client === null) {
			throw new Error();
		}
		return action(client, ...args);
	};

export const resolve = async <Result>(
	request: Promise<Result>,
	action: (result: Result | null, error: Error | null) => void
): Promise<Result> => {
	try {
		const result = await request;
		action(result, null);
		return result;
	} catch (error) {
		action(null, error as Error);
		throw error;
	}
};

export async function tryResolve<Result>(
	request: Promise<Result>,
	action: (result: Result | null, error: Error | null) => void
): Promise<Result | null> {
	try {
		const result = await request;
		action(result, null);
		return result;
	} catch (error) {
		action(null, error as Error);
		return null;
	}
}

export function invokeWithRetry<Result>(
	callback: () => Promise<Result>,
	timeoutMilliseconds: number
): [Promise<Result | null>, () => void] {
	const cancelled = { value: false };
	return [
		asyncInvokeWithRetry(callback, timeoutMilliseconds, cancelled),
		() => {
			cancelled.value = true;
		},
	];
}

async function asyncInvokeWithRetry<Result>(
	callback: () => Promise<Result>,
	timeoutMilliseconds: number,
	cancelled: { value: boolean }
): Promise<Result | null> {
	const retryKey = {};

	for (;;) {
		const promise: Promise<any> = Promise.race([
			callback(),
			new Promise((resolve) => setTimeout(resolve, timeoutMilliseconds, retryKey)),
		]);

		const result = await promise;
		if (result !== retryKey) {
			return result;
		}
		if (cancelled.value) {
			return null;
		}
	}
}
